import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClubPanel, PanelHead, PillButton, EmptyBlock, PINNED, SUBTLE, RED, textareaStyle, inputStyle } from './clubUi';
import ClubFeedPost from './ClubFeedPost';

/**
 * POSTS — the infinite feed (README §1): the admin composer on top (textarea #F5F5F5 r18,
 * `PIN IT` / `PINNED ✓` on #FFF8EA, `+ POLL` opens the inline poll form — question + two options
 * + CREATE POLL — `+ PORTAL`, `POST`), then the merged cards revealed a page at a time on the
 * sentinel, then `SCROLL FOR MORE` → `LOADING MORE…` → `THAT'S EVERYTHING.`
 */
function PollForm({ onCreate, onCancel, busy }) {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [deadline, setDeadline] = useState('');
  const valid = question.trim() && options.filter((o) => o.trim()).length >= 2;
  const field = { ...inputStyle, height: 42, padding: '0 16px', background: '#fff', fontSize: 13 };
  return (
    <div className="flex flex-col" style={{ gap: 8, padding: 14, borderRadius: 18, background: '#F5F5F5' }} data-testid="poll-form">
      <span className="uppercase" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em' }}>NEW POLL</span>
      <input value={question} onChange={(e) => setQuestion(e.target.value)} maxLength={300} style={field} placeholder="What do you want to know?" aria-label="Poll question" />
      {options.map((o, i) => (
        <span key={i} className="flex" style={{ gap: 6 }}>
          <input value={o} onChange={(e) => setOptions((prev) => prev.map((x, j) => (j === i ? e.target.value : x)))} maxLength={100} style={field} placeholder={`Option ${i + 1}`} aria-label={`Option ${i + 1}`} />
          {options.length > 2 && <PillButton variant="ghost" size={42} fontSize={10} onClick={() => setOptions((prev) => prev.filter((_, j) => j !== i))} aria-label={`Remove option ${i + 1}`}>✕</PillButton>}
        </span>
      ))}
      <span className="flex flex-wrap items-center" style={{ gap: 6 }}>
        {options.length < 10 && <PillButton variant="ghost" size={36} fontSize={10} weight={700} onClick={() => setOptions((prev) => [...prev, ''])}>+ ADD OPTION</PillButton>}
        <input type="datetime-local" value={deadline} onChange={(e) => setDeadline(e.target.value)} aria-label="Poll deadline" style={{ ...field, width: 'auto', height: 36, fontSize: 11 }} />
        <span className="ml-auto flex" style={{ gap: 6 }}>
          <PillButton variant="ghost" size={36} fontSize={10} weight={700} onClick={onCancel}>CANCEL</PillButton>
          <PillButton size={36} fontSize={10} disabled={!valid || busy} style={{ opacity: valid ? 1 : 0.35 }} onClick={() => onCreate({ question: question.trim(), options: options.map((o) => o.trim()).filter(Boolean), expiresAt: deadline ? new Date(deadline).toISOString() : null })} data-testid="poll-create">{busy ? '…' : 'CREATE POLL'}</PillButton>
        </span>
      </span>
    </div>
  );
}

export function ClubComposer({ club, state, profile, onPost, onCreatePoll, focusKey, mobile }) {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [pinned, setPinned] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pollOpen, setPollOpen] = useState(false);
  const [pollBusy, setPollBusy] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  useEffect(() => { if (focusKey) ref.current?.focus(); }, [focusKey]);

  const post = async () => {
    const content = text.trim();
    if (!content || busy) return;
    setBusy(true); setError(null);
    const r = await onPost({ content, isPinned: pinned });
    setBusy(false);
    if (r?.success === false) setError(r.error || 'Could not post');
    else { setText(''); setPinned(false); }
  };
  const createPoll = async (data) => {
    setPollBusy(true);
    const r = await onCreatePoll(data);
    setPollBusy(false);
    if (r?.success !== false) setPollOpen(false);
  };

  const signalsOn = club.memberSignalsEnabled !== false;
  return (
    <ClubPanel phone={mobile} data-testid="club-composer">
      <PanelHead title="POST TO THE CLUB" right={mobile ? undefined : 'MEMBERS GET A NOTIFICATION'} />
      <textarea ref={ref} value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={2000} placeholder="Say something to the club…" aria-label="Post to the club" data-testid="composer-input"
        style={{ ...textareaStyle, padding: '14px 16px', borderRadius: 18, resize: 'none' }} />
      {pollOpen && <PollForm onCreate={createPoll} onCancel={() => setPollOpen(false)} busy={pollBusy} />}
      <span className="flex flex-wrap items-center" style={{ gap: 6 }}>
        {state.isAdmin && <PillButton variant="tertiary" size={36} fontSize={10} weight={700} onClick={() => setPinned((v) => !v)} aria-pressed={pinned} style={{ background: pinned ? PINNED : undefined }} data-testid="composer-pin">{pinned ? 'PINNED ✓' : 'PIN IT'}</PillButton>}
        <PillButton variant="tertiary" size={36} fontSize={10} weight={700} onClick={() => setPollOpen((v) => !v)} aria-pressed={pollOpen} data-testid="composer-poll">+ POLL</PillButton>
        {signalsOn && <PillButton variant="tertiary" size={36} fontSize={10} weight={700} onClick={() => navigate(`/portals/new?club=${club.id}`)} data-testid="composer-signal">+ PORTAL</PillButton>}
        {error && <span style={{ fontSize: 11, color: RED }}>{error}</span>}
        <PillButton size={40} fontSize={11} className="ml-auto" style={{ opacity: text.trim() ? 1 : 0.35 }} onClick={post} disabled={!text.trim() || busy} data-testid="composer-post">{busy ? '…' : 'POST'}</PillButton>
      </span>
    </ClubPanel>
  );
}

export default function ClubFeed({
  club, state, profile, items, revealed, status, sentinelRef, loading,
  onPost, onCreatePoll, composeFocusKey, mobile = false, postHandlers,
}) {
  const visible = items.slice(0, revealed);
  const empty = !loading && items.length === 0;
  return (
    <div className="flex flex-col font-mono" style={{ gap: mobile ? 10 : 16 }} data-testid="club-feed">
      {state.isAdmin && <ClubComposer club={club} state={state} profile={profile} onPost={onPost} onCreatePoll={onCreatePoll} focusKey={composeFocusKey} mobile={mobile} />}

      {empty && (
        <ClubPanel phone={mobile}>
          <EmptyBlock title="NOTHING POSTED YET" body={state.isAdmin ? 'Write the first post. Members get a notification.' : 'The club hasn’t posted anything yet.'} action={state.isAdmin ? 'WRITE THE FIRST POST' : null} onAction={() => postHandlers?.onWriteFirst?.()} testId="write-first-post" />
        </ClubPanel>
      )}

      {visible.map((item) => (
        <ClubFeedPost key={item.key} item={item} state={state} currentUserId={profile?.id || profile?.userId} mobile={mobile} {...postHandlers} />
      ))}

      {items.length > 0 && (
        <div ref={sentinelRef} className="text-center" style={{ padding: '6px 0' }} data-testid="feed-sentinel">
          <span className="uppercase" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.2em', color: SUBTLE }} data-testid="feed-status">{status}</span>
        </div>
      )}
    </div>
  );
}
